import { useState, useRef } from "react";
import api, { BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, UploadCloud, X, ShieldCheck, Plus } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

const VALID = ["image/jpeg", "image/png", "image/webp", "image/gif"];
let uid = 0;

export default function UploadDialog({ open, onOpenChange, collectionId, onUpdated, remaining = 25 }) {
  const [items, setItems] = useState([]);
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const inputRef = useRef(null);

  const reset = () => { setItems([]); setConsent(false); setBusy(false); setProgress({ done: 0, total: 0 }); };

  const pick = (fileList) => {
    const files = Array.from(fileList || []);
    const valid = [];
    for (const f of files) {
      if (!VALID.includes(f.type)) { toast.error(`${f.name}: unsupported type`); continue; }
      if (f.size > 15 * 1024 * 1024) { toast.error(`${f.name}: too large (max 15MB)`); continue; }
      valid.push(f);
    }
    setItems((prev) => {
      const space = remaining - prev.length;
      if (space <= 0) { toast.error(`This collection is full (max ${remaining} more).`); return prev; }
      let toAdd = valid;
      if (valid.length > space) { toast.error(`Only ${space} more image${space === 1 ? "" : "s"} can be added.`); toAdd = valid.slice(0, space); }
      return [...prev, ...toAdd.map((f) => ({ id: ++uid, file: f, preview: URL.createObjectURL(f), title: f.name.replace(/\.[^.]+$/, "") }))];
    });
    if (inputRef.current) inputRef.current.value = "";
  };

  const removeItem = (id) => setItems((prev) => prev.filter((i) => i.id !== id));
  const setTitle = (id, val) => setItems((prev) => prev.map((i) => i.id === id ? { ...i, title: val } : i));

  const submit = async () => {
    if (items.length === 0 || !consent) return;
    setBusy(true);
    setProgress({ done: 0, total: items.length });
    let lastDoc = null;
    let failed = 0;
    for (let i = 0; i < items.length; i++) {
      const it = items[i];
      try {
        const fd = new FormData();
        fd.append("file", it.file);
        const up = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        const res = await api.post(`/collections/${collectionId}/items`, {
          archive_id: `upload-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          title: it.title.trim() || "My photo",
          image_url: `${BACKEND_URL}${up.data.file_url}`,
          format: "User upload",
          is_upload: true,
          copyright_consent: true,
        });
        lastDoc = res.data;
      } catch (e) {
        failed++;
      }
      setProgress({ done: i + 1, total: items.length });
    }
    if (lastDoc) onUpdated(lastDoc);
    if (failed === 0) toast.success(`${items.length} photo${items.length === 1 ? "" : "s"} added`);
    else toast.error(`${failed} upload${failed === 1 ? "" : "s"} failed`);
    reset();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="upload-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Upload your own photos</DialogTitle>
          <DialogDescription>Add images you own to this collection. Select several at once. You'll confirm you hold the rights before they're added.</DialogDescription>
        </DialogHeader>

        <button
          data-testid="upload-dropzone"
          onClick={() => inputRef.current?.click()}
          className="w-full border-2 border-dashed border-stoneline hover:border-brick rounded-xl py-8 flex flex-col items-center gap-2 text-muted-foreground hover:text-brick transition-colors"
        >
          <UploadCloud className="h-8 w-8" />
          <span className="text-sm font-medium">Click to choose images</span>
          <span className="text-xs">JPG, PNG, WEBP or GIF · up to 15MB each · {remaining - items.length} slot{remaining - items.length === 1 ? "" : "s"} left</span>
        </button>
        <input ref={inputRef} type="file" accept="image/*" multiple data-testid="upload-file-input" className="hidden" onChange={(e) => pick(e.target.files)} />

        {items.length > 0 && (
          <div className="space-y-2">
            {items.map((it) => (
              <div key={it.id} data-testid="upload-item-row" className="flex items-center gap-3 border border-stoneline rounded-lg p-2">
                <img src={it.preview} alt="" className="h-14 w-14 rounded object-cover shrink-0" />
                <Input value={it.title} onChange={(e) => setTitle(it.id, e.target.value)} placeholder="Title" className="flex-1 h-9" />
                <button onClick={() => removeItem(it.id)} className="text-muted-foreground hover:text-red-600 shrink-0 p-1.5"><X className="h-4 w-4" /></button>
              </div>
            ))}
            {remaining - items.length > 0 && (
              <button onClick={() => inputRef.current?.click()} className="text-sm text-brick font-medium inline-flex items-center gap-1 hover:underline">
                <Plus className="h-3.5 w-3.5" /> Add more
              </button>
            )}
          </div>
        )}

        <label className="flex items-start gap-3 bg-brick/5 border border-brick/20 rounded-lg p-3 cursor-pointer">
          <input type="checkbox" data-testid="copyright-consent-checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5 h-4 w-4 accent-brick" />
          <span className="text-sm text-ink flex items-start gap-1.5">
            <ShieldCheck className="h-4 w-4 text-brick shrink-0 mt-0.5" />
            I confirm that I own the copyright to {items.length > 1 ? "these images" : "this image"}, or otherwise have the right to share {items.length > 1 ? "them" : "it"}, and I grant Archive Nepal permission to display {items.length > 1 ? "them" : "it"} in this collection.
          </span>
        </label>

        <button
          data-testid="upload-submit-btn"
          onClick={submit}
          disabled={items.length === 0 || !consent || busy}
          className="inline-flex items-center justify-center gap-2 bg-brick hover:bg-brick-dark text-parchment px-5 py-2.5 rounded-full font-semibold transition-colors disabled:opacity-50"
        >
          {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Uploading {progress.done}/{progress.total}…</> : <><UploadCloud className="h-4 w-4" /> Upload & add {items.length > 0 ? `(${items.length})` : ""}</>}
        </button>
      </DialogContent>
    </Dialog>
  );
}
